// components/project/economic/StralciByCategoryWidget.jsx
// R.5.2 §2 — Widget read-only che consuma summary.stralci_by_category
// Nessuna nuova API: riusa i dati del contract-summary passato via prop.

import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Ban, Eye, EyeOff } from 'lucide-react';
import { salApi } from '../../../lib/api';

const formatCurrency = (v) => {
  const n0 = Number(v ?? 0);
  const n = Math.round(n0 * 100) === 0 ? 0 : n0;
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
};

// Ordine di visualizzazione desiderato
const CATEGORY_ORDER = [
  'rinuncia_cliente',
  'impossibilita_tecnica',
  'errore_progettuale',
  'sostituita_da_variante',
  'altro',
  '_unspecified',
];

const StralciByCategoryWidget = ({ projectId, summary: summaryProp, onCategoryClick }) => {
  const [loading, setLoading] = useState(!summaryProp);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(summaryProp || null);
  const [showAll, setShowAll] = useState(false);

  // Se non arriva via prop, carichiamo autonomamente (fallback)
  useEffect(() => {
    if (summaryProp) {
      setSummary(summaryProp);
      setLoading(false);
      return;
    }
    if (!projectId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await salApi.getContractSummary(projectId);
        if (!cancelled) setSummary(res.data);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 403) {
            setError({ code: 403, message: 'Non autorizzato.' });
          } else {
            setError({ code: e.response?.status || 0, message: e.response?.data?.detail || 'Errore nel caricamento' });
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId, summaryProp]);

  const { totalAmount, totalCount, rows } = useMemo(() => {
    const byCat = summary?.stralci_by_category || {};
    let totalAmount = 0;
    let totalCount = 0;
    const rows = CATEGORY_ORDER
      .filter((k) => byCat[k] !== undefined)
      .map((code) => {
        const bucket = byCat[code] || { total: 0, count: 0, label: code };
        const amount = Number(bucket.total || 0);
        const count = Number(bucket.count || 0);
        totalAmount += amount;
        totalCount += count;
        return { code, label: bucket.label || code, amount, count };
      });
    const rowsWithPct = rows.map((r) => ({
      ...r,
      pct: totalAmount > 0 ? (r.amount / totalAmount) * 100 : 0,
    }));
    return { totalAmount, totalCount, rows: rowsWithPct };
  }, [summary]);

  if (loading) {
    return (
      <Card className="border border-[#EDEDED]" data-testid="stralci-widget-skeleton">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-2">
            <div className="h-4 w-1/3 bg-gray-200 rounded" />
            <div className="h-16 bg-gray-100 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border border-red-200 bg-red-50" data-testid="stralci-widget-error">
        <CardContent className="p-4 text-sm text-red-800">
          Riepilogo stralci non disponibile: {error.message}
        </CardContent>
      </Card>
    );
  }

  if (totalCount === 0 && !showAll) {
    return (
      <Card className="border border-[#EDEDED]" data-testid="stralci-widget-empty">
        <CardContent className="p-4 flex items-center gap-3 text-sm text-[#737373]">
          <Ban className="w-4 h-4 text-gray-400" />
          Nessuna lavorazione stralciata su questo progetto.
        </CardContent>
      </Card>
    );
  }

  const visibleRows = showAll ? rows : rows.filter((r) => r.count > 0);

  return (
    <Card className="border border-[#EDEDED]" data-testid="stralci-by-category-widget">
      <CardContent className="p-4">
        {/* Header con totali */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-600" />
            <h4 className="text-sm font-bold uppercase tracking-wide text-[#3C3C3C]">
              Stralci per categoria
            </h4>
          </div>
          <button
            className="text-xs text-[#0078C8] hover:underline inline-flex items-center gap-1"
            onClick={() => setShowAll((v) => !v)}
            data-testid="stralci-toggle-show-all"
          >
            {showAll ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showAll ? 'Nascondi categorie a zero' : 'Mostra tutte'}
          </button>
        </div>

        {/* KPI totali */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="p-3 rounded-md bg-red-50 border border-red-100" data-testid="stralci-total-amount-card">
            <div className="text-xs text-red-700 uppercase tracking-wide">Totale stralci</div>
            <div className="text-lg font-bold text-red-800">{formatCurrency(totalAmount)}</div>
          </div>
          <div className="p-3 rounded-md bg-gray-50 border border-gray-200" data-testid="stralci-total-count-card">
            <div className="text-xs text-[#737373] uppercase tracking-wide">Lavorazioni stralciate</div>
            <div className="text-lg font-bold text-[#3C3C3C]">{totalCount}</div>
          </div>
        </div>

        {/* Tabella per categoria */}
        <div className="rounded-md border border-[#EDEDED] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[#737373] text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-2">Categoria</th>
                <th className="text-right p-2 w-16">N°</th>
                <th className="text-right p-2 w-28">Importo</th>
                <th className="text-right p-2 w-16">%</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan="4" className="p-3 text-center text-xs text-[#9CA3AF]">
                    Nessuna categoria da mostrare.
                  </td>
                </tr>
              )}
              {visibleRows.map((r) => {
                const clickable = r.count > 0 && typeof onCategoryClick === 'function';
                return (
                  <tr
                    key={r.code}
                    className={`border-t border-[#EDEDED] ${clickable ? 'cursor-pointer hover:bg-[#FAFAFA]' : ''} ${r.count === 0 ? 'text-[#9CA3AF]' : ''}`}
                    onClick={clickable ? () => onCategoryClick(r) : undefined}
                    data-testid={`stralci-row-${r.code}`}
                  >
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${r.count > 0 ? 'bg-red-500' : 'bg-gray-300'}`}
                        />
                        <span className={r.count > 0 ? 'text-[#3C3C3C]' : ''}>{r.label}</span>
                      </div>
                      {/* Barra */}
                      {r.count > 0 && r.pct > 0 && (
                        <div className="mt-1 h-1 bg-red-100 rounded-full overflow-hidden">
                          <div
                            className="h-1 bg-red-500 rounded-full"
                            style={{ width: `${Math.min(100, r.pct)}%` }}
                            data-testid={`stralci-bar-${r.code}`}
                          />
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-right font-mono">{r.count}</td>
                    <td className="p-2 text-right font-mono">{formatCurrency(r.amount)}</td>
                    <td className="p-2 text-right font-mono text-xs">
                      {totalAmount > 0 ? `${r.pct.toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default StralciByCategoryWidget;
